import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AccountBalance as BankIcon, KeyboardArrowDown as ExpandIcon, AttachFile as AttachIcon, Block as ExcludeIcon, RestartAlt as RestoreIcon, Close as RemoveIcon, Add as AddIcon, SwapHoriz as SwapIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

interface BankConnectionRecord {
  id: string;
  institution_name: string | null;
  account_holder_name: string | null;
  account_number: string | null;
  account_mask: string | null;
  account_iban: string | null;
}

interface BankTransactionRow {
  id: string;
  bank_connection_id?: string;
  amount: string;
  iso_currency_code: string | null;
  date: string;
  name: string | null;
  merchant_name: string | null;
  category: string | null;
  pending: boolean;
  excluded?: boolean;
  /** User-assigned free-text label (e.g. "Rent") edited inline from the Tag column — see
   * BankTransaction.tag. Replaces the always-empty Category column (TrueLayer's own
   * classification field is unpopulated for most providers/transactions). */
  tag?: string | null;
  linked_proofs?: any[];
  /** A confirmed, unlinked receipt/invoice shown here as its own pseudo-account ("Manual entry")
   * rather than a real TrueLayer row — see AccountingPage.tsx's mergedConnections/
   * mergedTransactions. It's already the record of the expense itself, so it gets a "Self-
   * reported" badge instead of the Upload-proof controls (there's nothing to attach proof of). */
  isCash?: boolean;
  /** The full invoice/receipt record behind a cash row — passed to onViewProof so clicking its
   * name opens the same viewer used for a real linked proof, and to onRemoveCash so it can tell
   * a document-less manual entry apart from a real uploaded one. */
  sourceInvoice?: { id: string; pdf_filename?: string | null; original_filename?: string | null; [key: string]: any };
}

interface BankConnectionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  connections: BankConnectionRecord[];
  transactions: BankTransactionRow[];
  onUploadProof: (bankTransactionId: string) => void;
  onViewProof: (proof: any) => void;
  /** Marks/unmarks a real live-feed transaction as "not relevant to my business account" —
   * persisted, reversible; excluded from every Income/Expenses sum but never deleted. */
  onToggleExcluded: (transactionId: string, excluded: boolean) => void;
  /** Applies the same hide/show state to several transactions at once — the "hide/show the
   * other N transactions tagged 'X' too?" bulk prompt. */
  onBulkToggleExcluded: (transactionIds: string[], excluded: boolean) => void;
  /** Sets/clears a transaction's tag (see BankTransaction.tag). Pass '' to clear. */
  onSetTag: (transactionId: string, tag: string) => void;
  /** Applies the same tag to several transactions at once — the "tag the other N transactions
   * with this same description too?" bulk-tag prompt. */
  onBulkSetTag: (transactionIds: string[], tag: string) => void;
  /** Removes a self-reported "cash" entry — the caller decides based on the record's own
   * pdf_filename/original_filename: a document-less manual entry is deleted outright, while a
   * real uploaded receipt/invoice just reverts to being a normal Receipts/Invoices entry (still
   * fully counted), no longer shown here. */
  onRemoveCash: (invoice: { id: string; pdf_filename?: string | null; original_filename?: string | null }) => void;
  /** Which connection(s) to pre-select when the dialog opens — mirrors whatever the page's own
   * "Accounts" filter is currently scoped to (e.g. just Starling), so opening this dialog from
   * there shows the same accounts rather than always resetting to "all". Only ever changes the
   * dialog's own local selection (via the existing chip toggles below) — never writes back to
   * the page's filter. Pass null/omit for the old "always all" default. */
  initialSelectedConnectionIds?: string[] | null;
  /** Which "YYYY-MM" month buckets to expand by default — mirrors the page's own period filter
   * (e.g. This quarter's 3 months), computed from the same monthly-trend data the chart already
   * shows, so opening this dialog focuses on exactly what the page is currently filtered to.
   * Every other month still renders as a collapsed (not removed) Accordion below, so the user can
   * still browse them. Falls back to expanding just the most recent month when none match (e.g.
   * a period with no transactions yet). */
  initialExpandedMonthKeys?: string[];
  /** Opens the "Add a new transaction" flow (AddCashTransactionDialog, owned by the page) —
   * either picking an already-uploaded receipt/invoice or adding a blank manual entry. */
  onAddNewTransaction: () => void;
  /** Inline edits (description, amount, direction) on a cash row directly in the table below —
   * see the isCash-only click-to-edit cells. */
  onEditCashTransaction: (invoiceId: string, updates: { contractor_name?: string; total?: string; direction?: 'money_out' | 'money_in' }) => void;
}

const fmtAmount = (val: string | number | null | undefined, currency: string | null | undefined) => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: currency || 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(val || 0));
  } catch {
    return `${Number(val || 0).toFixed(2)} ${currency || ''}`.trim();
  }
};

// The live-feed counterpart to BankStatementDetailDialog — same header stat grid, same "one block
// per calendar month" shape, opened as a popup rather than an inline expand so both bank paths
// (a connected account and an uploaded statement) present transactions the same way.
//
// Accepts every connected account at once (not just one) — `transactions` is already merged
// across all of them (see AccountingPage.tsx's fetchBankData), and each row carries its own
// `bank_connection_id` so it can be attributed back to the right account via a "Bank" column.
const BankConnectionDetailDialog: React.FC<BankConnectionDetailDialogProps> = ({ open, onClose, connections, transactions, onUploadProof, onViewProof, onToggleExcluded, onBulkToggleExcluded, onSetTag, onBulkSetTag, onRemoveCash, initialSelectedConnectionIds, initialExpandedMonthKeys, onAddNewTransaction, onEditCashTransaction }) => {
  const theme = useTheme();
  const { palette } = theme;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Inline tag editing (see BankTransaction.tag) — which row (if any) is currently showing its
  // Autocomplete editor, and the in-progress typed value for it. existingTags feeds the
  // dropdown of already-used tags across every transaction currently loaded, so picking "Rent"
  // once makes it a one-click option on every other transaction afterward.
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState('');
  const existingTags = Array.from(new Set(transactions.map(t => (t.tag || '').trim()).filter(Boolean))).sort();

  // Inline editing for a cash row's own Description/Amount (see AddCashTransactionDialog — a
  // manually-added or newly-linked cash transaction starts blank/zero and is filled in here).
  // Only ever applies to isCash rows — a real bank-fed transaction's description/amount come
  // from the bank itself and aren't editable.
  const [editingCashField, setEditingCashField] = useState<{ id: string; field: 'description' | 'amount' } | null>(null);
  const [cashFieldDraft, setCashFieldDraft] = useState('');
  const commitCashDescription = (txn: BankTransactionRow) => {
    setEditingCashField(null);
    const value = cashFieldDraft.trim();
    if (!value || value === (txn.merchant_name || txn.name || '')) return;
    onEditCashTransaction(txn.id, { contractor_name: value });
  };
  const commitCashAmount = (txn: BankTransactionRow) => {
    setEditingCashField(null);
    const parsed = parseFloat(cashFieldDraft.replace(/[£,]/g, ''));
    if (Number.isNaN(parsed) || parsed.toFixed(2) === Math.abs(Number(txn.amount)).toFixed(2)) return;
    onEditCashTransaction(txn.id, { total: parsed.toFixed(2) });
  };
  const flipCashDirection = (txn: BankTransactionRow) => {
    const isCredit = Number(txn.amount) < 0;
    onEditCashTransaction(txn.id, { direction: isCredit ? 'money_out' : 'money_in' });
  };
  const txnDescription = (t: BankTransactionRow) => (t.merchant_name || t.name || '').trim();
  // After tagging one transaction, offer to bulk-apply the same change to every other
  // transaction (across all months/accounts currently loaded, not just this one's group)
  // sharing an identical description — e.g. tagging one "To Trading 212" row also offers every
  // other "To Trading 212" row; clearing a tag likewise offers to clear it from every other row
  // that still carries that same tag.
  const [bulkTagPrompt, setBulkTagPrompt] = useState<{ description: string; tag: string; matchIds: string[]; action: 'set' | 'clear' } | null>(null);
  const commitTag = (txn: BankTransactionRow, value: string) => {
    setEditingTagId(null);
    const newTag = value.trim();
    const oldTag = (txn.tag || '').trim();
    if (newTag === oldTag) return;
    onSetTag(txn.id, newTag);
    const desc = txnDescription(txn);
    if (!desc) return;
    if (newTag) {
      const matches = transactions.filter(t => t.id !== txn.id && !t.isCash && txnDescription(t) === desc && (t.tag || '').trim() !== newTag);
      if (matches.length > 0) {
        setBulkTagPrompt({ description: desc, tag: newTag, matchIds: matches.map(t => t.id), action: 'set' });
      }
    } else if (oldTag) {
      const matches = transactions.filter(t => t.id !== txn.id && !t.isCash && txnDescription(t) === desc && (t.tag || '').trim() === oldTag);
      if (matches.length > 0) {
        setBulkTagPrompt({ description: desc, tag: oldTag, matchIds: matches.map(t => t.id), action: 'clear' });
      }
    }
  };

  // After hiding/restoring one transaction, if it has a tag, offer to apply the same hide/show
  // state to every other transaction (across all months/accounts currently loaded) sharing that
  // same tag — e.g. hiding one "trading"-tagged row also offers every other "trading"-tagged row.
  const [bulkExcludePrompt, setBulkExcludePrompt] = useState<{ tag: string; excluded: boolean; matchIds: string[] } | null>(null);
  const commitExcluded = (txn: BankTransactionRow, excluded: boolean) => {
    onToggleExcluded(txn.id, excluded);
    const tag = (txn.tag || '').trim();
    if (!tag) return;
    const matches = transactions.filter(t => t.id !== txn.id && !t.isCash && (t.tag || '').trim() === tag && !!t.excluded !== excluded);
    if (matches.length > 0) {
      setBulkExcludePrompt({ tag, excluded, matchIds: matches.map(t => t.id) });
    }
  };

  // Which connection(s) to show — one, the other, or both. Defaults to whatever the page's own
  // "Accounts" filter was scoped to when this dialog was opened (initialSelectedConnectionIds),
  // or "all" if that wasn't provided/didn't narrow anything — every time the dialog (re)opens or
  // the connection list itself changes (e.g. after a disconnect), so a filter picked on a
  // previous visit never silently hides an account on the next one.
  const connectionIdsKey = connections.map(c => c.id).join(',');
  const [selectedIds, setSelectedIds] = useState<string[]>(() => connections.map(c => c.id));
  useEffect(() => {
    const valid = initialSelectedConnectionIds?.filter(id => connectionIdsKey.split(',').includes(id));
    setSelectedIds(valid && valid.length > 0 ? valid : (connectionIdsKey ? connectionIdsKey.split(',') : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionIdsKey, open]);

  const handleAccountsChange = (next: string[]) => {
    if (next.length === 0) return; // always keep at least one bank selected
    setSelectedIds(next);
  };

  if (connections.length === 0) return null;

  const connById = new Map(connections.map(c => [c.id, c]));
  const bankLabel = (txn: BankTransactionRow) => {
    const conn = txn.bank_connection_id ? connById.get(txn.bank_connection_id) : undefined;
    if (!conn) return '—';
    return conn.institution_name || 'Bank';
  };

  const visibleTransactions = connections.length > 1
    ? transactions.filter(t => t.bank_connection_id && selectedIds.includes(t.bank_connection_id))
    : transactions;
  const showBankColumn = connections.length > 1 && selectedIds.length > 1;

  const sorted = [...visibleTransactions].sort((a, b) => b.date.localeCompare(a.date));
  const monthGroups: { key: string; label: string; txns: BankTransactionRow[] }[] = [];
  for (const txn of sorted) {
    const key = txn.date.slice(0, 7);
    let group = monthGroups.find(g => g.key === key);
    if (!group) {
      const label = new Date(`${key}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      group = { key, label, txns: [] };
      monthGroups.push(group);
    }
    group.txns.push(txn);
  }
  const feedCurrency = sorted[0]?.iso_currency_code || 'GBP';

  // Expand whichever month(s) match the page's own period filter (e.g. This quarter's 3 months)
  // — every other month still renders as a collapsed Accordion, just not expanded by default.
  // Falls back to "just the most recent month" when nothing matches (e.g. a period with no
  // transactions yet), same as the pre-existing default before this filter existed.
  const hasMatchingExpandedKeys = !!initialExpandedMonthKeys?.length && monthGroups.some(g => initialExpandedMonthKeys.includes(g.key));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} sx={{ '& .MuiDialog-paper': { maxWidth: 945 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BankIcon sx={{ color: palette.tokens.muted }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {connections.length === 1
                ? (connections[0].institution_name || 'Connected bank')
                : 'Bank transactions'}
            </Typography>
            {connections.length === 1 && (
              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
                {connections[0].account_holder_name || 'Account holder unavailable'}
              </Typography>
            )}
          </Box>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAddNewTransaction} sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}>
            Add new transaction
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
        {/* ── header, mirroring BankStatementDetailDialog's own summary block. Account
            number/IBAN only make sense for a single account, so they're swapped out for a
            per-account breakdown once there's more than one connection. ── */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2,
          p: 2, mb: 2.5, bgcolor: palette.tokens.surfaceSoft, borderRadius: 2,
        }}>
          {connections.length === 1 ? (
            <>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Account number</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{connections[0].account_number || (connections[0].account_mask ? `····${connections[0].account_mask}` : '—')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>IBAN</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink, wordBreak: 'break-all' }}>{connections[0].account_iban || '—'}</Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}>
              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Accounts
              </Typography>
              <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                <Select
                  multiple
                  value={selectedIds}
                  onChange={(e) => handleAccountsChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => (selected as string[])
                    .map(id => {
                      const c = connById.get(id);
                      return c ? `${c.institution_name || 'Bank'}${c.account_mask ? ` ····${c.account_mask}` : ''}` : '';
                    })
                    .filter(Boolean)
                    .join(', ')}
                  sx={{ fontSize: 13, fontWeight: 600, bgcolor: palette.tokens.canvas, '& .MuiSelect-select': { py: 0.75 } }}
                >
                  {connections.map(c => (
                    <MenuItem key={c.id} value={c.id} dense>
                      <Checkbox size="small" checked={selectedIds.includes(c.id)} />
                      <ListItemText primary={`${c.institution_name || 'Bank'}${c.account_mask ? ` ····${c.account_mask}` : ''}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Currency</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{feedCurrency}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Transactions</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{visibleTransactions.length}</Typography>
          </Box>
        </Box>

        {monthGroups.length === 0 ? (
          <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', py: 4 }}>No transactions yet.</Typography>
        ) : (
          <Box sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, overflow: 'hidden' }}>
            {monthGroups.map((group, idx) => {
              const countedTxns = group.txns.filter(t => !t.excluded);
              const incoming = countedTxns.filter(t => Number(t.amount) < 0).reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
              const outgoing = countedTxns.filter(t => Number(t.amount) > 0).reduce((s, t) => s + Number(t.amount), 0);
              const net = incoming - outgoing;
              const monthCurrency = group.txns[0]?.iso_currency_code || 'GBP';
              // VAT for the whole month \u2014 same per-row derivation as the table's own VAT (IN)/
              // VAT (OUT) columns, summed and split by each transaction's own direction.
              const txnVat = (t: BankTransactionRow) => t.isCash
                ? Number(t.sourceInvoice?.tax || 0)
                : (t.linked_proofs || []).reduce((sum, p) => sum + Number(p.tax || 0), 0);
              const vatIn = countedTxns.filter(t => Number(t.amount) < 0).reduce((s, t) => s + txnVat(t), 0);
              const vatOut = countedTxns.filter(t => Number(t.amount) > 0).reduce((s, t) => s + txnVat(t), 0);
              return (
                <Accordion
                  key={group.key}
                  disableGutters
                  elevation={0}
                  defaultExpanded={hasMatchingExpandedKeys ? initialExpandedMonthKeys!.includes(group.key) : idx === 0}
                  sx={{
                    '&:before': { display: 'none' },
                    borderBottom: idx < monthGroups.length - 1 ? `1px solid ${palette.tokens.hairline}` : 'none',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink }}>{group.label}</Typography>
                        <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
                          {group.txns.length} transaction{group.txns.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2.5 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ display: 'block', color: palette.tokens.mutedSoft }}>In</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: palette.tokens.success }}>{fmtAmount(incoming, monthCurrency)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ display: 'block', color: palette.tokens.mutedSoft }}>Out</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: palette.tokens.ink }}>{fmtAmount(outgoing, monthCurrency)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                          <Typography variant="caption" sx={{ display: 'block', color: palette.tokens.mutedSoft }}>Net</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: net >= 0 ? palette.tokens.success : palette.tokens.error }}>
                            {net >= 0 ? '+' : ''}{fmtAmount(net, monthCurrency)}
                          </Typography>
                        </Box>
                        {(vatOut > 0 || vatIn > 0) && (
                          <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                            <Typography variant="caption" sx={{ display: 'block', color: palette.tokens.mutedSoft }}>VAT</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: palette.tokens.mutedSoft }}>
                              {vatOut > 0 && `Out ${fmtAmount(vatOut, monthCurrency)}`}
                              {vatOut > 0 && vatIn > 0 ? ' · ' : ''}
                              {vatIn > 0 && `In ${fmtAmount(vatIn, monthCurrency)}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Date</TableCell>
                          <TableCell sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Description</TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>Credit <Box component="span" sx={{ fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(IN)</Box></TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>Debit <Box component="span" sx={{ fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(OUT)</Box></TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>VAT <Box component="span" sx={{ fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(IN)</Box></TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>VAT <Box component="span" sx={{ fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(OUT)</Box></TableCell>
                          {showBankColumn && (
                            <TableCell sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Bank</TableCell>
                          )}
                          <TableCell sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Proof</TableCell>
                          <TableCell sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, width: 90, maxWidth: 90 }}>Tag</TableCell>
                          <TableCell align="center" sx={{ verticalAlign: 'top', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, width: 36, pl: 0.5, pr: 1 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.txns.map(txn => {
                          const amt = Number(txn.amount);
                          const isCredit = amt < 0; // this codebase's stored convention: negative = incoming
                          const proofs = txn.linked_proofs || [];
                          // VAT for the row — derived from whichever invoice(s) are attached as
                          // proof of it (summed if more than one), or from the source invoice
                          // behind a self-reported cash row. Never stored independently, so it
                          // always matches whatever's currently on the invoice/receipt itself.
                          const vatAmount = txn.isCash
                            ? Number(txn.sourceInvoice?.tax || 0)
                            : proofs.reduce((sum, p) => sum + Number(p.tax || 0), 0);
                          return (
                            <TableRow key={txn.id} hover sx={txn.excluded ? { opacity: 0.45 } : undefined}>
                              <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{txn.date}</TableCell>
                              <TableCell sx={{ fontSize: 12.5 }}>
                                {txn.isCash && editingCashField?.id === txn.id && editingCashField.field === 'description' ? (
                                  <TextField
                                    size="small" autoFocus value={cashFieldDraft}
                                    onChange={(e) => setCashFieldDraft(e.target.value)}
                                    onBlur={() => commitCashDescription(txn)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') commitCashDescription(txn);
                                      if (e.key === 'Escape') setEditingCashField(null);
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 12.5, py: 0.25 } }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    onClick={txn.isCash ? () => { setEditingCashField({ id: txn.id, field: 'description' }); setCashFieldDraft(txn.merchant_name || txn.name || ''); } : undefined}
                                    sx={{
                                      fontWeight: 600, fontSize: 12.5, color: palette.tokens.ink,
                                      cursor: txn.isCash ? 'pointer' : 'default',
                                      '&:hover': txn.isCash ? { textDecoration: 'underline' } : undefined,
                                    }}
                                  >
                                    {txn.merchant_name || txn.name || 'Transaction'}
                                  </Typography>
                                )}
                                {txn.pending && (
                                  <Chip label="Pending" size="small" sx={{ mt: 0.25, bgcolor: alpha(palette.tokens.warning, 0.15), color: palette.tokens.warning, fontWeight: 700, fontSize: 10, height: 16 }} />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.success }}>
                                {txn.isCash && isCredit ? (
                                  editingCashField?.id === txn.id && editingCashField.field === 'amount' ? (
                                    <TextField
                                      size="small" autoFocus value={cashFieldDraft} type="number"
                                      onChange={(e) => setCashFieldDraft(e.target.value)}
                                      onBlur={() => commitCashAmount(txn)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitCashAmount(txn);
                                        if (e.key === 'Escape') setEditingCashField(null);
                                      }}
                                      sx={{ width: 90, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.25, textAlign: 'right' } }}
                                    />
                                  ) : (
                                    <Box component="span" onClick={() => { setEditingCashField({ id: txn.id, field: 'amount' }); setCashFieldDraft(Math.abs(amt).toFixed(2)); }} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                      {fmtAmount(Math.abs(amt), txn.iso_currency_code)}
                                    </Box>
                                  )
                                ) : !txn.isCash && isCredit ? (
                                  fmtAmount(Math.abs(amt), txn.iso_currency_code)
                                ) : txn.isCash ? (
                                  <Tooltip title="Switch to money in">
                                    <IconButton size="small" onClick={() => flipCashDirection(txn)} sx={{ color: palette.tokens.mutedSoft }}>
                                      <SwapIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.ink }}>
                                {txn.isCash && !isCredit ? (
                                  editingCashField?.id === txn.id && editingCashField.field === 'amount' ? (
                                    <TextField
                                      size="small" autoFocus value={cashFieldDraft} type="number"
                                      onChange={(e) => setCashFieldDraft(e.target.value)}
                                      onBlur={() => commitCashAmount(txn)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitCashAmount(txn);
                                        if (e.key === 'Escape') setEditingCashField(null);
                                      }}
                                      sx={{ width: 90, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.25, textAlign: 'right' } }}
                                    />
                                  ) : (
                                    <Box component="span" onClick={() => { setEditingCashField({ id: txn.id, field: 'amount' }); setCashFieldDraft(amt.toFixed(2)); }} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                      {fmtAmount(amt, txn.iso_currency_code)}
                                    </Box>
                                  )
                                ) : !txn.isCash && !isCredit ? (
                                  fmtAmount(amt, txn.iso_currency_code)
                                ) : txn.isCash ? (
                                  <Tooltip title="Switch to money out">
                                    <IconButton size="small" onClick={() => flipCashDirection(txn)} sx={{ color: palette.tokens.mutedSoft }}>
                                      <SwapIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>
                                {isCredit && vatAmount > 0 ? fmtAmount(vatAmount, txn.iso_currency_code) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>
                                {!isCredit && vatAmount > 0 ? fmtAmount(vatAmount, txn.iso_currency_code) : '—'}
                              </TableCell>
                              {showBankColumn && (
                                <TableCell sx={{ fontSize: 11.5, color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>{bankLabel(txn)}</TableCell>
                              )}
                              <TableCell>
                                {txn.isCash ? (
                                  <Chip
                                    size="small"
                                    label={txn.merchant_name || txn.name || 'Self-reported'}
                                    onClick={() => onViewProof(txn.sourceInvoice)}
                                    sx={{ fontSize: 10, height: 20, cursor: 'pointer', bgcolor: alpha(palette.tokens.primary, 0.1), color: palette.tokens.primary, fontWeight: 700 }}
                                  />
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    {proofs.map((p) => (
                                      <Chip
                                        key={p.id}
                                        size="small"
                                        label={p.contractor_name || (p.document_type === 'Receipt' ? 'Receipt' : 'Invoice')}
                                        onClick={() => onViewProof(p)}
                                        sx={{ fontSize: 10, height: 20, cursor: 'pointer', bgcolor: alpha(palette.tokens.primary, 0.1), color: palette.tokens.primary, fontWeight: 700 }}
                                      />
                                    ))}
                                    <Chip
                                      size="small"
                                      icon={<AttachIcon sx={{ fontSize: '13px !important' }} />}
                                      label={proofs.length > 0 ? 'Add' : 'Upload proof'}
                                      onClick={() => onUploadProof(txn.id)}
                                      variant="outlined"
                                      sx={{ fontSize: 10, height: 20, cursor: 'pointer', borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11.5, width: 90, maxWidth: 90, overflow: 'hidden' }}>
                                {txn.isCash ? '—' : editingTagId === txn.id ? (
                                  <Autocomplete
                                    freeSolo
                                    disableClearable
                                    forcePopupIcon={false}
                                    size="small"
                                    options={existingTags}
                                    inputValue={tagDraft}
                                    onInputChange={(_e, val) => setTagDraft(val)}
                                    onChange={(_e, val) => commitTag(txn, val || '')}
                                    onBlur={() => commitTag(txn, tagDraft)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitTag(txn, tagDraft); }
                                      if (e.key === 'Escape') setEditingTagId(null);
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} autoFocus variant="standard" placeholder="Tag…" sx={{ '& .MuiInputBase-input': { fontSize: 11.5 } }} />
                                    )}
                                    slotProps={{ popper: { style: { width: 180 } } }}
                                    sx={{ width: 90, maxWidth: 90 }}
                                  />
                                ) : txn.tag ? (
                                  <Chip
                                    size="small"
                                    label={txn.tag}
                                    onClick={() => { setTagDraft(txn.tag || ''); setEditingTagId(txn.id); }}
                                    onDelete={() => commitTag(txn, '')}
                                    sx={{ fontSize: 10, height: 20, maxWidth: 88, cursor: 'pointer', bgcolor: alpha(palette.tokens.primary, 0.1), color: palette.tokens.primary, fontWeight: 700 }}
                                  />
                                ) : (
                                  <Box
                                    component="span"
                                    onClick={() => { setTagDraft(''); setEditingTagId(txn.id); }}
                                    sx={{ cursor: 'pointer', fontSize: 11, color: palette.tokens.hairline, whiteSpace: 'nowrap', '&:hover': { color: palette.tokens.muted } }}
                                  >
                                    + Add
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell align="center" sx={{ width: 36, pl: 0.5, pr: 1 }}>
                                {txn.isCash ? (
                                  <Tooltip title={
                                    txn.sourceInvoice && !txn.sourceInvoice.pdf_filename && !txn.sourceInvoice.original_filename
                                      ? 'Delete this transaction'
                                      : 'Remove from transactions — goes back to just the Receipts/Invoices list'
                                  }>
                                    <IconButton size="small" onClick={() => txn.sourceInvoice && onRemoveCash(txn.sourceInvoice)}>
                                      <RemoveIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={txn.excluded ? 'Show this transaction' : 'Hide this transaction'}>
                                    <IconButton size="small" onClick={() => commitExcluded(txn, !txn.excluded)}>
                                      {txn.excluded ? <RestoreIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft }} /> : <ExcludeIcon sx={{ fontSize: 15, color: palette.tokens.hairline }} />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Bulk-tag prompt — offered right after tagging or clearing one transaction's tag, if any
          others share an identical description (see commitTag above). */}
      <Dialog open={!!bulkTagPrompt} onClose={() => setBulkTagPrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {bulkTagPrompt?.action === 'clear' ? 'Remove tag from similar transactions too?' : 'Tag similar transactions too?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            {bulkTagPrompt?.action === 'clear' ? (
              <>There {bulkTagPrompt.matchIds.length === 1 ? 'is 1 other transaction' : `are ${bulkTagPrompt.matchIds.length} other transactions`} with description "{bulkTagPrompt.description}" tagged "{bulkTagPrompt.tag}". Remove the tag from {bulkTagPrompt.matchIds.length === 1 ? 'it' : 'them'} too?</>
            ) : (
              <>There {bulkTagPrompt && bulkTagPrompt.matchIds.length === 1 ? 'is 1 other transaction' : `are ${bulkTagPrompt?.matchIds.length} other transactions`} with description "{bulkTagPrompt?.description}". Tag {bulkTagPrompt && bulkTagPrompt.matchIds.length === 1 ? 'it' : 'them'} as "{bulkTagPrompt?.tag}" as well?</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkTagPrompt(null)} sx={{ color: palette.tokens.muted }}>No, just this one</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (bulkTagPrompt) onBulkSetTag(bulkTagPrompt.matchIds, bulkTagPrompt.action === 'clear' ? '' : bulkTagPrompt.tag);
              setBulkTagPrompt(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            {bulkTagPrompt?.action === 'clear' ? 'Yes, remove from all' : 'Yes, tag them all'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk-hide/show prompt — offered right after hiding or restoring one tagged transaction,
          if any others share that same tag (see commitExcluded above). */}
      <Dialog open={!!bulkExcludePrompt} onClose={() => setBulkExcludePrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {bulkExcludePrompt?.excluded ? 'Hide similar transactions too?' : 'Show similar transactions too?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            There {bulkExcludePrompt && bulkExcludePrompt.matchIds.length === 1 ? 'is 1 other transaction' : `are ${bulkExcludePrompt?.matchIds.length} other transactions`} tagged "{bulkExcludePrompt?.tag}". {bulkExcludePrompt?.excluded ? 'Hide' : 'Show'} {bulkExcludePrompt && bulkExcludePrompt.matchIds.length === 1 ? 'it' : 'them'} too?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkExcludePrompt(null)} sx={{ color: palette.tokens.muted }}>No, just this one</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (bulkExcludePrompt) onBulkToggleExcluded(bulkExcludePrompt.matchIds, bulkExcludePrompt.excluded);
              setBulkExcludePrompt(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            {bulkExcludePrompt?.excluded ? 'Yes, hide them all' : 'Yes, show them all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BankConnectionDetailDialog;
